import React, { useState, useEffect } from "react";
import type { Question } from "../types";

interface QuizProps {
    quizId: string;
    username: string;
    onChangeUser: () => void;
}

interface Result {
    username: string;
    score: number;
    timestamp: number;
}

export default function Quiz({ quizId, username, onChangeUser }: QuizProps) {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [loading, setLoading] = useState(true);
    const [copySuccess, setCopySuccess] = useState("");

    const [scoreboard, setScoreboard] = useState<Result[]>([]);
    const [hasStarted, setHasStarted] = useState(false);

    // Fetch questions from the backend
    const fetchQuestions = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/quiz/${quizId}`);
            if (!res.ok) throw new Error("Quiz not found");
            const data = await res.json();
            setQuestions(data);
        } catch (error) {
            console.error("Failed to fetch questions:", error);
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    const submitScore = async (finalScore: number) => {
        try {
            await fetch(`/api/quiz/${quizId}/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, score: finalScore }),
            });
            localStorage.setItem(`quiz_completed_${quizId}_${username}`, finalScore.toString());
            fetchScoreboard();
        } catch (error) {
            console.error("Failed to submit score:", error);
        }
    };

    const fetchScoreboard = async () => {
        try {
            const res = await fetch(`/api/quiz/${quizId}/results`);
            const data = await res.json();
            setScoreboard(data);
        } catch (error) {
            console.error("Failed to fetch scoreboard:", error);
        }
    };

    useEffect(() => {
        if (quizId) {
            const savedScore = localStorage.getItem(`quiz_completed_${quizId}_${username}`);
            if (savedScore !== null) {
                setScore(parseInt(savedScore, 10));
                setShowResult(true);
                fetchScoreboard();
                fetchQuestions(); // Need questions for total count
            } else {
                fetchQuestions();
            }
        }
    }, [quizId]);

    const handleStartQuiz = () => {
        setHasStarted(true);
        setCurrentIndex(0);
        setScore(0);
        setShowResult(false);
        setSelectedOption(null);
        setIsAnswered(false);
    }

    const handleNextQuestion = () => {
        if (currentIndex + 1 < questions.length) {
            setCurrentIndex((prev) => prev + 1);
            setSelectedOption(null);
            setIsAnswered(false);
        } else {
            setShowResult(true);
            submitScore(score);
        }
    };

    const handleOptionClick = (option: string) => {
        if (isAnswered) return;

        setSelectedOption(option);
        setIsAnswered(true);

        const currentQuestion = questions[currentIndex];
        const isCorrect = currentQuestion && option === currentQuestion.answer;

        let newScore = score;
        if (isCorrect) {
            newScore = score + 1;
            setScore(newScore);

            // Auto advance only if correct
            setTimeout(() => {
                if (currentIndex + 1 < questions.length) {
                    setCurrentIndex((prev) => prev + 1);
                    setSelectedOption(null);
                    setIsAnswered(false);
                } else {
                    setShowResult(true);
                    submitScore(newScore);
                }
            }, 1000);
        }
        // If incorrect, we do NOT auto advance. User must click "Next".
    };

    const handleShare = async () => {
        const url = window.location.href;
        const shareData = {
            title: 'TUS Wars Çekişmesi',
            text: `Bu yarışmada ${score}/${questions.length} yaptım! Beni geçebilir misin?`,
            url: url
        };

        // Tier 1: Native Share (Mobile)
        if (navigator.share) {
            try {
                await navigator.share(shareData);
                setCopySuccess("Paylaştığın için teşekkürler!");
                setTimeout(() => setCopySuccess(""), 3000);
                return;
            } catch (err) {
                console.log('User cancelled share or share failed:', err);
                // Continue to fallbacks if share fails generally, but usually share 
                // just might be cancelled by user. We can still try to copy link as backup.
            }
        }

        // Tier 2: Modern Clipboard API (Secure Contexts)
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(url);
                setCopySuccess("Link kopyalandı! Arkadaşlarınla paylaş!");
                setTimeout(() => setCopySuccess(""), 3000);
                return;
            } catch (err) {
                console.warn('Clipboard writeText failed, trying fallback:', err);
            }
        }

        // Tier 3: Legacy Fallback (HTTP/LAN/Older Browsers)
        try {
            const textArea = document.createElement("textarea");
            textArea.value = url;

            // Avoid scrolling to bottom
            textArea.style.top = "0";
            textArea.style.left = "0";
            textArea.style.position = "fixed";

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                const msg = successful ? 'Link kopyalandı! Arkadaşlarınla paylaş!' : 'Link kopyalanamadı 😢';
                setCopySuccess(msg);
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
                setCopySuccess("Link kopyalanamadı 😢");
            }

            document.body.removeChild(textArea);
            setTimeout(() => setCopySuccess(""), 3000);
        } catch (err) {
            console.error('Final fallback failed', err);
            setCopySuccess("Link otomatik paylaşılamadı");
            setTimeout(() => setCopySuccess(""), 3000);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <div className="text-xl font-semibold text-gray-600 animate-pulse">
                    TUS Düellosu hazırlanıyor...
                </div>
            </div>
        );
    }

    if (questions.length === 0) {
        return (
            <div className="text-center p-8 bg-white rounded-xl shadow-md max-w-md mx-auto">
                <h3 className="text-xl font-bold text-red-500 mb-4">Yarışma Bulunamadı 😢</h3>
                <p className="text-gray-600 mb-6">Bu link geçersiz veya süresi dolmuş olabilir.</p>
                <a href="/" className="text-blue-600 hover:underline">Yeni yarışma oluştur</a>
            </div>
        );
    }

    // Initial Start Screen
    if (!hasStarted && !showResult) {
        return (
            <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg overflow-hidden md:max-w-2xl p-8 text-center animate-fade-in relative">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                    Hazır mısın, {username}?
                    <button onClick={onChangeUser} className="ml-3 text-sm text-blue-500 hover:text-blue-700 underline font-normal">(Değiştir)</button>
                </h2>
                <p className="text-gray-600 mb-8">
                    {questions.length} zorlu soru seni bekliyor. Bol şans!
                </p>

                <div className="flex flex-col gap-4">
                    <button
                        onClick={handleStartQuiz}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 shadow-md transform hover:scale-[1.02]"
                    >
                        Yarışmaya Başla 🚀
                    </button>

                    <button
                        onClick={handleShare}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 px-6 rounded-lg transition duration-300 shadow-sm"
                    >
                        🔗 Yarışmayı Paylaş
                    </button>
                </div>
                {copySuccess && <div className="mt-4 text-green-600 text-sm font-medium animate-bounce">{copySuccess}</div>}
            </div>
        )
    }

    if (showResult) {
        return (
            <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg overflow-hidden md:max-w-2xl p-8 text-center animate-fade-in relative">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Yarışma Tamamlandı!</h2>
                <p className="text-xl text-gray-600 mb-6">
                    Puanın: <span className="font-bold text-blue-600">{score}</span> /{" "}
                    <span className="font-bold">{questions.length}</span>
                </p>

                <div className="mb-8">
                    {score > 1 ? (
                        <div className="text-green-500 font-bold text-2xl">Harika İş! 🌟</div>
                    ) : score > 0 ? (
                        <div className="text-yellow-500 font-bold text-2xl">İyi Deneme! 👍</div>
                    ) : (
                        <div className="text-red-500 font-bold text-2xl">Daha Çok Çalışmalısın! 💪</div>
                    )}
                </div>

                {/* Scoreboard */}
                {scoreboard.length > 0 && (
                    <div className="mb-8 text-left">
                        <h3 className="text-lg font-bold text-gray-700 mb-3 border-b pb-2">🏆 Liderlik Tablosu</h3>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                            {scoreboard.map((result, idx) => (
                                <div key={idx} className={`flex justify-between items-center p-2 rounded ${result.username === username && result.score === score ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                                    <div className="flex items-center">
                                        <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold mr-2 ${idx < 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-200 text-gray-600'}`}>
                                            {idx + 1}
                                        </span>
                                        <span className="font-medium text-gray-700 truncate max-w-[120px]">{result.username}</span>
                                    </div>
                                    <span className="font-bold text-gray-800">{result.score}/{questions.length}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-4">
                    <button
                        onClick={handleShare}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-full transition duration-300 shadow-md"
                    >
                        🔗 Sonucu Paylaş
                    </button>
                    {copySuccess && <div className="text-green-600 text-sm font-medium animate-bounce">{copySuccess}</div>}

                    <a
                        href="/"
                        className="text-gray-500 hover:text-gray-700 font-medium py-2 transition"
                    >
                        Yeni Yarışma Oluştur
                    </a>
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentIndex];

    if (!currentQuestion) {
        return null;
    }

    const isIncorrect = isAnswered && selectedOption !== currentQuestion.answer;
    // Show explanation only if incorrect answers AND explanation exists
    const showExplanation = isIncorrect && !!currentQuestion.explanation;

    return (
        <div className="max-w-2xl mx-auto p-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden relative">
                {/* Progress Bar */}
                <div className="bg-gray-200 h-2 w-full">
                    <div
                        className="bg-blue-500 h-2 transition-all duration-300 ease-out"
                        style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                    ></div>
                </div>

                <div className="p-8">
                    <div className="flex justify-between items-center mb-6 text-sm text-gray-500 font-medium">
                        <span>Soru {currentIndex + 1} / {questions.length}</span>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-8 leading-tight pr-8">
                        {currentQuestion.question}
                    </h2>

                    <div className="space-y-4">
                        {currentQuestion.options.map((option, index) => {
                            let buttonClass = "w-full text-left p-4 rounded-xl border-2 transition-all duration-200 font-medium text-lg ";

                            if (isAnswered) {
                                if (currentQuestion && option === currentQuestion.answer) {
                                    buttonClass += "bg-green-100 border-green-500 text-green-800";
                                } else if (option === selectedOption) {
                                    buttonClass += "bg-red-100 border-red-500 text-red-800 opacity-60";
                                } else {
                                    buttonClass += "border-gray-200 text-gray-400 opacity-50";
                                }
                            } else {
                                buttonClass += "border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-gray-700 active:scale-[0.98]";
                            }

                            return (
                                <button
                                    key={index}
                                    onClick={() => handleOptionClick(option)}
                                    disabled={isAnswered}
                                    className={buttonClass}
                                >
                                    <div className="flex items-center">
                                        <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-white border-2 border-current mr-4 text-sm font-bold opacity-70">
                                            {String.fromCharCode(65 + index)}
                                        </span>
                                        {option}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {showExplanation && (
                        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl text-left animate-fade-in">
                            <h4 className="font-bold text-blue-800 mb-2 flex items-center">
                                <span className="text-xl mr-2">💡</span> Açıklama
                            </h4>
                            <p className="text-blue-900 text-sm leading-relaxed whitespace-pre-wrap">
                                {currentQuestion.explanation?.replace(/\\n/g, '\n')}
                            </p>
                        </div>
                    )}
                </div>

                <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 text-center text-gray-400 text-sm min-h-[70px] flex items-center justify-center">
                    {isAnswered ? (
                        isIncorrect ? (
                            <button
                                onClick={handleNextQuestion}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-sm"
                            >
                                {currentIndex + 1 < questions.length ? "Sonraki Soru ➡️" : "Sonuçları Gör 📊"}
                            </button>
                        ) : (
                            "Doğru! Sonraki soruya geçiliyor..."
                        )
                    ) : (
                        "Doğru şıkkı işaretleyiniz"
                    )}
                </div>
            </div>
        </div>
    );
    /* End of Component */
}
